import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/screens/InteractionSearchList.css';
import MiniTimeline from '../components/MiniTimeline';
import SectionScoresBar from '../components/SectionScoresBar';
import { fetchData, api, extractApiError } from '../services/api';
import { PATH_SEGMENTS } from '../config/paths';
import { getSentimentIcon } from '../utils/iconMappings';
import { removeFilterValue, updateFilterValue, filtersToQueryParams } from '../utils/filterUtils';
import { formatDateOnly, formatTimeOnly, formatDuration } from '../utils/dateUtils';
import { useLayout } from '../contexts/LayoutContext';
import FilterButton from '../components/FilterButton';
import SearchFilterControl from '../components/SearchFilterControl';
import { interactionFilters } from '../config/filterConfigs';
import SearchList, { SearchListColumn, SearchListConfig } from '../components/SearchList';
import { FilterAction } from '../components/FilterBar';
import { usePagination } from '../hooks/usePagination';
import { useKPILoading } from '../hooks/useKPILoading';
import { NumericLabel } from '../components/common';
import PileItem from '../components/common/PileItem';
import type { } from '../types/api';
import { config } from 'config';
import { liveAssistPrepareReplay } from '../services/liveAssistClient';
import PostCallStatus from '../components/PostCallStatus';
import {
  getPostCallStatuses,
  PostCallJob,
} from '../services/postCallClient';

interface Interaction {
  id: number;
  external_id: string;
  name: string;
  duration: number;
  time: string;
  agent_first_name?: string;
  agent_last_name?: string;
  customer_first_name?: string;
  customer_last_name?: string;
  language?: string;
  channel?: string;
  bound?: string;
  username?: string;
  create_time?: string;
  call_score?: number;
  customer_name?: string;
  agent_name?: string;
  customer_id?: string;
  agent_id?: string;
}

interface Execution {
  id: number;
  interaction_id: number;
  transcript_id: number;
  execution_time: string;
  genai_tool: string;
  search_tool: string;
  mmt: boolean;
  eval: boolean;
  adherence: boolean;
  summary: boolean;
}

interface InteractionData {
  interaction: Interaction;
  transcript_id?: number;
  evaluation_id: number | null;
  transcript_evaluation_id_list?: TranscriptEvaluationMapping[];
  // Optional embedded payloads from INTERACTIONS response
  evaluation?: any;
  transcript?: ApiTimelineResponse;
  // Array where each entry is { [kpiName]: KpiData }
  kpis?: Array<Record<string, any>>;
  // Executions array: [Execution[], pagination]
  executions?: [Execution[], any];
}

interface TranscriptEvaluationMapping {
  transcript_id: number;
  evaluation_id_list: number[];
}

interface ResponseData {
  filters: {
    [key: string]: any;
  };
  interactions: InteractionData[];  // Changed from object to array
  pagination: {
    current_page: number;
    total_pages: number;
    total_items: number;
    items_per_page: number;
    order_by: string;
    order_direction: 'asc' | 'desc';
  };
}

// Let's consolidate our interfaces to avoid conflicts
// SectionScore for the component
interface ComponentSectionScore {
  name: string;
  score: number;
  maxScore: number;
}

// Speech segment for the timeline
interface SpeechSegment {
  id: number;
  transcript_id: number;
  speech: string;
  start_ms: number;
  end_ms: number;
  duration: number;
  speaker_name: string;
}

// Define the TranscriptData interface
interface TranscriptData {
  id: number;
  name: string;
  language: string;
  time: string | null;
  transcriber?: string;
  transcription_language?: boolean;
  transcriber_config?: string;
}

interface MomentData {
  id: number; 
  name: string;
  sentiment: string;
}

interface SpeechTextMoment {
  [key: string]: MomentData;
}

// Interface for the timeline data
interface ApiTimelineResponse {
  transcript: TranscriptData;
  speech_texts: SpeechSegment[];
  mmts: SpeechTextMoment;
  evaluation?: any;
}

// Update state types
interface TimelineDataState {
  [key: number]: SpeechSegment[];
}


// Add a new interface for the section score response
interface SectionScore {
  id: number;
  points: number;
  evaluation_id: number;
  section_id: number;
  name: string;
}

interface EvaluationScoreResponse {
  evaluation: {
    form: {
      id: number;
      form_id: number;
      name: string;
      points: number;
    };
    sections: SectionScore[];
  };
}

// Use a single state object to store both section scores and total scores
interface ScoreData {
  sectionScores: Record<string, number>;
  totalScore: number;
}

// Add the formatSectionScores function that was missing
const formatSectionScores = (scores: any): ComponentSectionScore[] => {
  try {
    if (!scores || typeof scores !== 'object') return [];
    
    // Convert scores object to array
    const scoresArray = Object.entries(scores).map(([name, score]) => ({
      name,
      score: typeof score === 'number' ? score : 0,
      maxScore: 20 // Match the maxScore prop
    }));
    
    // Return only the first 5 section scores
    return scoresArray.slice(0, 5);
  } catch (err) {
    console.error('Error formatting section scores:', err);
    return []; // Return empty array on error
  }
};

// Add these interfaces near the top with other interfaces
interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

const InteractionSearchList: React.FC = () => {
  const { updateHeaderProps } = useLayout();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filters, setFilters] = useState<{ [key: string]: any }>({});
  const [interactions, setInteractions] = useState<InteractionData[]>([]);
  const [timelineData, setTimelineData] = useState<TimelineDataState>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [sectionScores, setSectionScores] = useState<Record<number, Record<string, number>>>({});
  const [totalScores, setTotalScores] = useState<Record<number, number>>({});
  const [postCallJobs, setPostCallJobs] = useState<Record<number, PostCallJob>>({});
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    updateHeaderProps({
      pageTitle: 'Interactions',
      showInteractionExId: false
    });
  }, [updateHeaderProps]);

  // KPI loading for interactions; parent decides which need loading
  const baseKpiNames = ['call_score', 'script_violations'];
  const interactionIdsAll = interactions.map(item => item.interaction.id);
  const interactionIdsKey = interactionIdsAll.join(',');

  useEffect(() => {
    if (!interactionIdsKey) {
      setPostCallJobs({});
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const loadStatuses = async () => {
      try {
        const jobs = await getPostCallStatuses(
          interactionIdsKey.split(',').map(Number)
        );
        if (cancelled) return;
        setPostCallJobs(jobs);
        if (Object.values(jobs).some(job => job.status === 'queued' || job.status === 'running')) {
          timer = setTimeout(loadStatuses, 3000);
        }
      } catch (statusError) {
        console.warn('Unable to load post-call analysis status', statusError);
      }
    };

    loadStatuses();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [interactionIdsKey]);
  
  // Build preloaded KPI map from interactions response if present
  const preloadedKpiMap = React.useMemo(() => {
    const map: Record<string, Record<string, any>> = {};
    interactions.forEach(item => {
      const idStr = String(item.interaction.id);
      const src: any = (item as any).kpis;
      if (!src) return;
      const kpisForInteraction: Record<string, any> = {};
      if (Array.isArray(src)) {
        // New contract: array of dicts, values are already KpiData
        (src as Array<Record<string, any>>).forEach(entry => {
          const key = Object.keys(entry)[0];
          if (key) {
            kpisForInteraction[key] = (entry as any)[key];
          }
        });
      } else if (typeof src === 'object') {
        // Be defensive: support object-map form (legacy)
        Object.entries(src as Record<string, any>).forEach(([key, val]) => {
          if (typeof val === 'string') {
            try {
              kpisForInteraction[key] = JSON.parse(val);
            } catch {
              kpisForInteraction[key] = val;
            }
          } else {
            kpisForInteraction[key] = val;
          }
        });
      }
      map[idStr] = kpisForInteraction;
    });
    return map;
  }, [interactions]);

  // Compute which interactions are missing any of the required KPIs
  const interactionIdsNeedingKpis = React.useMemo(() => {
    const ids: number[] = [];
    interactions.forEach(item => {
      const idStr = String(item.interaction.id);
      const pre = preloadedKpiMap[idStr] || {};
      const needs = baseKpiNames.some(name => pre[name] === undefined || pre[name] === null);
      if (needs) ids.push(item.interaction.id);
    });
    return ids;
  }, [interactions, preloadedKpiMap]);

  const {
    kpiDataMap,
    kpiLoading,
    kpiError,
    getKpiData
  } = useKPILoading({
    interactionIds: interactionIdsNeedingKpis,
    kpiNames: baseKpiNames,
    preloadedKpiMap
  });

  // Use the pagination hook
  const {
    paginationState: { currentPage, totalPages, itemsPerPage, sortConfig },
    handlePageChange,
    handleSort,
    paginationToQueryParams,
    queryParamsToPagination,
    updatePagination
  } = usePagination({ disableAutoUrlUpdate: true }, { field: 'id', direction: 'asc' });

  useEffect(() => {
    // Get query parameters from the URL
    const queryParams = new URLSearchParams(location.search);
    // console.log('URL query parameters:', Object.fromEntries(queryParams.entries()));
    console.log('INTERACTIONS endpoint', api(PATH_SEGMENTS.INTERACTION));
    console.log('config.api.baseUrl', config.api.baseUrl);

    // Use these parameters for the initial API call
    if (queryParams.toString()) {
      // If there are query parameters in URL, use them
      fetchInteractions(queryParams);
    } else {
      // Otherwise, fetch with no filters
      fetchInteractions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);


  // Updated formatDate to handle the 'time' field
  const formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  // Updated formatTime to handle the 'time' field
  const formatTime = (dateString: string): string => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  // Compact date format for display
  const formatDateCompact = (dateString: string): string => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // If it's today, show just time
    if (diffDays === 1 && date.toDateString() === now.toDateString()) {
      const timePart =  new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).format(date);
      return `Today at ${timePart}`;
    }
    
    // If it's this year, show month, day and time
    if (date.getFullYear() === now.getFullYear()) {
      const datePart = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric'
      }).format(date);
      const timePart = new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).format(date);
      return `${datePart} at ${timePart}`;
    }
    
    // Otherwise show month, year and time
    const datePart = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      year: '2-digit'
    }).format(date);
    const timePart = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date);
    return `${datePart} at ${timePart}`;
  };

  // Detailed date format for tooltip
  const formatDateDetailed = (dateString: string): string => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  // Updated formatDuration to handle seconds
  const formatDuration = (seconds: number): string => {
    if (seconds === undefined || seconds === null) return 'N/A';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes === 0) {
      return `${remainingSeconds}s`;
    }
    
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Helper to combine first and last name
  const getFullName = (firstName: string | undefined, lastName: string | undefined): string => {
    if (!firstName && !lastName) return 'Unknown';
    return `${firstName || ''} ${lastName || ''}`.trim();
  };

  // Add useEffect to monitor pagination states
  useEffect(() => {
    console.log('Pagination states updated:', {
      currentPage,
      totalPages,
      itemsPerPage
    });
  }, [currentPage, totalPages, itemsPerPage, sortConfig]);

  // Convert filters to query parameters
  const filtersToQueryParams = (filters: Record<string, any>) => {
    const queryParams = new URLSearchParams();
    // Add filters to query params
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Handle array values like transcription_keywords
        value.forEach(item => {
          queryParams.append(key, item);
        });
      } else if (value !== null && value !== undefined && value !== '') {
        // Only add non-empty values
        queryParams.set(key, String(value));
      }
    });
    return queryParams;

  };
  // Convert query parameters to filters
  const queryParamsToFilters = (queryParams: URLSearchParams) => {
    const filters: Record<string, any> = {};
    queryParams.forEach((value, key) => {
      filters[key] = value;
    });
    return filters;
  };

  // Fetch interactions
  const fetchInteractions = async (queryParams?: URLSearchParams) => {
    setLoading(true);
    setError(null);
    try {
      // Build query string from filters if not provided
      if (!queryParams) {
        queryParams = filtersToQueryParams(filters);
        queryParams = paginationToQueryParams(queryParams, currentPage, itemsPerPage, sortConfig);
      }

      // Log the query parameters being sent
      console.log('Sending query parameters to API:', Object.fromEntries(queryParams.entries()));
      
      // Append query parameters to the URL
      const queryString = queryParams.toString();
      const url = `${api(PATH_SEGMENTS.INTERACTION)}${queryString ? '?' + queryString : ''}`;
            
      // Use the fetchData function with the endpoint
      const data: ResponseData = await fetchData(url);
      
      console.log("Full API Response:", data);
      
      // Set filters from API response
      setFilters(data.filters || {});
      
      // Process interactions based on transcript_evaluation_id_list, prefer embedded data
      let processedInteractions: InteractionData[] = [];

      // Small helper to build a row with optional embedded payloads
      const buildInteractionRow = (
        base: InteractionData,
        transcriptId: number,
        evaluationId: number | null
      ): InteractionData => ({
        interaction: base.interaction,
        transcript_id: transcriptId,
        evaluation_id: evaluationId,
        evaluation: (base as any).evaluation || undefined,
        transcript: (base as any).transcript || undefined,
        kpis: (base as any).kpis || undefined,
        executions: (base as any).executions || undefined,
      });
      
      for (const interactionData of data.interactions || []) {
        if (interactionData.transcript_evaluation_id_list && interactionData.transcript_evaluation_id_list.length > 0) {
          // Use the new structure to create rows for each interaction-transcript-evaluation combination
          for (const mapping of interactionData.transcript_evaluation_id_list) {
            if (mapping.evaluation_id_list && mapping.evaluation_id_list.length > 0) {
              // Create a row for each evaluation
              for (const evaluationId of mapping.evaluation_id_list) {
                console.log("adding row:", interactionData.interaction, mapping.transcript_id, evaluationId);
                processedInteractions.push(
                  buildInteractionRow(interactionData as any, mapping.transcript_id, evaluationId)
                );
              }
            } else {
              // Create a row even if no evaluations
              console.log("adding row:", interactionData.interaction, mapping.transcript_id, null);
              processedInteractions.push(
                buildInteractionRow(interactionData as any, mapping.transcript_id, null)
              );
            }
          }
        } else {
          // Fallback to original structure
          processedInteractions.push(interactionData as any);
        }
      }
      
      console.log("Processed Interactions Array:", processedInteractions);
      setInteractions(processedInteractions);
      
      // Update pagination state with more detailed logging
      if (data.pagination) {
        console.log("Pagination Data from API:", data.pagination);
        console.log("Setting current page to:", data.pagination.current_page);
        console.log("Setting total pages to:", data.pagination.total_pages);
        console.log("Setting items per page to:", data.pagination.items_per_page);
        
        // Update the pagination hook state with API response data
        updatePagination({
          currentPage: data.pagination.current_page,
          totalPages: data.pagination.total_pages,
          itemsPerPage: data.pagination.items_per_page,
          sortConfig: {
            field: data.pagination.order_by,
            direction: data.pagination.order_direction
          }
        });
      } else {
        console.warn("No pagination data in API response");
      }
      
      console.log("API Response:", data);
      console.log("Interactions:", processedInteractions);
      console.log("Filters:", data.filters);
      
    } catch (error) {
      console.error('Error fetching interactions:', error);
      setInteractions([]);
      setError("Failed to load interactions");
    } finally {
      setLoading(false);
    }
  };

  // Fixed fetchTimelineData function
  const fetchTimelineData = async (transcriptId: number): Promise<SpeechSegment[]> => {
    if (!transcriptId || isNaN(transcriptId)) {
      console.error("Invalid transcript ID:", transcriptId);
      return [];
    }
    
    try {
      // Use the TRANSCRIPT endpoint with listing=true parameter
      const endpoint = `${api(PATH_SEGMENTS.TRANSCRIPT, transcriptId.toString())}?listing=true`;
      
      const response = await fetch(endpoint);
      const data = await response.json();
      
      
      // Check for speech_texts in the expected location based on the API response structure
      let speechTexts = [];
      if (data.speech_texts) {
        speechTexts = data.speech_texts;
      } else if (data.transcript && data.transcript.speech_texts) {
        speechTexts = data.transcript.speech_texts;
      }
      
      return speechTexts;
    } catch (error) {
      console.error(`Error fetching timeline data for transcript ${transcriptId}:`, error);
      return [];
    }
  };

  // Then in your component where you load data:
  useEffect(() => {

    let cancelled = false;
    // Load timeline data for each interaction that has a transcript
    const loadTimelineData = async () => {
      const newTimelineData: TimelineDataState = {};
      
      for (const item of interactions) {
        // Prefer embedded transcript speech_texts if available
        const embedded = (item as any).transcript;
        if (embedded) {
          const speechTexts = (embedded as any).speech_texts || (embedded as any).transcript?.speech_texts || [];
          newTimelineData[item.interaction.id] = speechTexts as any;
          continue;
        }

        // Safely get the transcript ID and fetch if needed
        const transcriptId = getTranscriptId(item);
        if (transcriptId) {
          try {
            const speechData = await fetchTimelineData(transcriptId);
            newTimelineData[item.interaction.id] = speechData;
          } catch (error) {
            console.error(`Error loading timeline for interaction ${item.interaction.id}:`, error);
          }
        }
      }
      
      if (!cancelled) {
        setTimelineData(newTimelineData);
      }
    };
    
    if (interactions.length > 0) {
      loadTimelineData();
    }
  }, [interactions]);

  // Fix the fetchScoreData function to ensure the evaluation endpoint is called with clear logging
  const fetchScoreData = async () => {
    try {
      
      if (!interactions || interactions.length === 0) {
        console.warn("No interactions to fetch scores for");
        return;
      }
      
      const newSectionScores: Record<number, Record<string, number>> = {};
      const newTotalScores: Record<number, number> = {};
      
      for (const item of interactions) {
        if (!item || !item.interaction || !item.interaction.id) continue;
        
        const interactionId = item.interaction.id;
        const evaluationId = item.evaluation_id;
        
        // Prefer embedded evaluation if present; else fetch by evaluationId
        const embeddedEval: any = (item as any).evaluation;
        if (embeddedEval) {
          const evalObj = embeddedEval.evaluation ? embeddedEval.evaluation : embeddedEval;
          const scores: Record<string, number> = {};
          if (evalObj && Array.isArray(evalObj.sections)) {
            evalObj.sections.forEach((section: any) => {
              if (section && section.name) {
                scores[section.name] = section.points || 0;
              }
            });
          }
          newSectionScores[interactionId] = scores;
          if (evalObj && evalObj.form) {
            newTotalScores[interactionId] = evalObj.form.points || 0;
          }
          continue;
        }

        if (evaluationId) {
          // Define the evaluation endpoint URL
          const evaluationEndpoint = `${api(PATH_SEGMENTS.EVALUATION, evaluationId.toString())}?scores=true&all=false&insights=false&remarks=false&questions=false&form=false`;
          try {
            const response = await fetch(evaluationEndpoint);
            if (!response.ok) {
              console.error(`Error response from evaluation API: ${response.status} ${response.statusText}`);
              continue;
            }
            const evaluationData = await response.json();
            const evalObj = evaluationData && evaluationData.evaluation ? evaluationData.evaluation : evaluationData;
            const scores: Record<string, number> = {};
            if (evalObj && Array.isArray(evalObj.sections)) {
              evalObj.sections.forEach((section: any) => {
                if (section && section.name) {
                  scores[section.name] = section.points || 0;
                }
              });
            }
            newSectionScores[interactionId] = scores;
            if (evalObj && evalObj.form) {
              newTotalScores[interactionId] = evalObj.form.points || 0;
            }
          } catch (err) {
            console.error(`Error fetching evaluation ${evaluationId}:`, err);
          }
        }
      }
      
      // Set both states from the API call
      setSectionScores(newSectionScores);
      setTotalScores(newTotalScores);
    } catch (error) {
      console.error("Error in fetchScoreData:", error);
      setError("Failed to load score data");
    }
  };

  // Make sure this function is called in the useEffect 
  useEffect(() => {
    
    if (interactions && interactions.length > 0) {
      try {
        fetchScoreData();
      } catch (err) {
        console.error("Error calling fetchScoreData:", err);
      }
    }
  }, [interactions]);

  const updateUrlAndFetch = (newFilters: Record<string, any>) => {

    let params = filtersToQueryParams(newFilters)
    if (searchQuery) {
      params.append('transcription_keywords', searchQuery);
    }
    
    // Update URL without reloading the page
    const url = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ path: url }, '', url);
    
    // Call fetchInteractions with URLSearchParams
    fetchInteractions(params);
  };

  // Simplified handleFilterChange - only handles component-specific logic
  const handleFilterChange = (newFilters: Record<string, any>) => {
    setFilters(newFilters);
    updateUrlAndFetch(newFilters);
  };

  // Update handleSearch to use utility function
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    let queryParams = filtersToQueryParams(filters)
    if (searchQuery) {
      queryParams.append('transcription_keywords', searchQuery);
    }
    
    // Update URL with the search query and filters
    navigate({
      pathname: location.pathname,
      search: queryParams.toString()
    }, { replace: true });
    
    // Clear the search field
    setSearchQuery('');
    // Fetch interactions with the updated query parameters
    fetchInteractions(queryParams);
  };

  const handleInteractionClick = (interactionId: number) => {
    navigate(`/interaction/${interactionId}`);
  };

  const handleReplayClick = async (interactionId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const data = await liveAssistPrepareReplay(interactionId);
      const id = data.interaction_id ?? interactionId;
      navigate(`/live-assist/replay/${id}`);
    } catch (err) {
      console.error('Failed to prepare replay', err);
      // Fall back to replay route; page can still load existing TA if present.
      navigate(`/live-assist/replay/${interactionId}`);
    }
  };

  const handleDownloadTranscriptClick = async (interactionId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const url = api(PATH_SEGMENTS.INTERACTION, interactionId, 'transcript-download');
      const token = localStorage.getItem('token');
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const err = new Error(`HTTP ${res.status}`) as Error & { response?: Response };
        err.response = res;
        throw err;
      }
      const blob = await res.blob();
      const dispo = res.headers.get('Content-Disposition');
      let filename = `transcript-${interactionId}.txt`;
      const m = dispo && /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(dispo);
      if (m?.[1]) {
        filename = decodeURIComponent(m[1].replace(/"/g, '').trim());
      }
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(href);
    } catch (err) {
      const { message } = await extractApiError(err as Error);
      console.error('Transcript download failed', err);
      window.alert(message);
    }
  };

  // Add a helper function to determine the score color class
  const getScoreClass = (score: number): string => {
    if (score >= 90) return 'score-excellent';
    if (score >= 75) return 'score-good';
    if (score >= 60) return 'score-average';
    if (score >= 40) return 'score-poor';
    return 'score-critical';
  };

  // Helper function to get transcript ID from different possible locations
  const getTranscriptId = (item: InteractionData): number | undefined => {
    // Check the item itself first
    if (item.transcript_id) return item.transcript_id;
    
    // Try to cast interaction to access possible properties
    const interaction = item.interaction as any;
    if (interaction && interaction.transcript_id) return interaction.transcript_id;
    
    // Check other possible locations based on your actual data structure
    return undefined;
  };

  // Wrapper functions that handle both pagination state and data fetching
  const handlePageChangeWithFetch = (newPage: number) => {
    handlePageChange(newPage);
    
    // Build query parameters and fetch data
    let queryParams = filtersToQueryParams(filters);
    if (searchQuery) {
      queryParams.append('transcription_keywords', searchQuery);
    }
    queryParams = paginationToQueryParams(queryParams, newPage, itemsPerPage, sortConfig);
    
    fetchInteractions(queryParams);
  };

  const handleSortWithFetch = (field: string) => {
    const newDirection: 'asc' | 'desc' = sortConfig.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    const newSortConfig = { field, direction: newDirection };
    
    console.log("handleSortWithFetch newSortConfig", newSortConfig);
    // Update pagination state
    updatePagination({ 
      sortConfig: newSortConfig,
      currentPage: 1 // Reset to first page when sorting
    });
    
    // Build query parameters and fetch data
    let queryParams = filtersToQueryParams(filters);
    if (searchQuery) {
      queryParams.append('transcription_keywords', searchQuery);
    }
    queryParams = paginationToQueryParams(queryParams, 1, itemsPerPage, newSortConfig);
    
    fetchInteractions(queryParams);
  };

  // Show loading state
  if (loading) {
    return <div className="loading">Loading interactions...</div>;
  }

  // Show error state
  if (error) {
    return <div className="error-message">{error}</div>;
  }

  // Helper function to count executions by type
  const countExecutions = (item: InteractionData, type: 'mmt' | 'eval' | 'adherence' | 'summary'): number => {
    console.log("InteractionData item", item, " type", type);
    if (!item.executions || !Array.isArray(item.executions)) {
      return 0;
    }
    console.log("item.executions", item.executions);
    const executionsArray = item.executions;
    if (!executionsArray || !Array.isArray(executionsArray)) {
      return 0;
    }
    return executionsArray.filter(exec => exec[type] === true).length;
  };

  // SearchList Configuration
  const searchListConfig: SearchListConfig = {

    columns: [
      {
        key: 'id',
        header: 'ID',
        width: '0.5fr',
        sortable: true,
        render: (item: InteractionData) => (
          item.interaction.id
        )
      },
      {
        key: 'name',
        header: 'Name',
        width: '1.5fr',
        sortable: true,
        render: (item: InteractionData) => (
          <div className="interaction-name-cell">
            {item.interaction.external_id || item.interaction.name || 'N/A'}
          </div>
        )
      },
      {
        key: 'agent',
        header: 'Agent',
        subtext: 'Customer',
        width: '1fr',
        sortable: true,
        render: (item: InteractionData) => (
          <div className="agent-customer-cell">
            <div className="agent-name">{getFullName(item.interaction.agent_first_name, item.interaction.agent_last_name)}</div>
            <div className="customer-name">{getFullName(item.interaction.customer_first_name, item.interaction.customer_last_name)}</div>
          </div>
        )
      },
      {
        key: 'date_time',
        header: 'Date & Time',
        width: '1.25fr',
        sortable: true,
        render: (item: InteractionData) => (
          <div className="date-time-cell">
            {formatDateCompact(item.interaction.time)}
          </div>
        ),
        tooltip: (item: InteractionData) => formatDateDetailed(item.interaction.time)
      },
      {
        key: 'duration',
        header: 'Duration',
        subtext: 'mm:ss',
        width: '1fr',
        sortable: true,
        render: (item: InteractionData) => (
          <div className="duration-cell">
            {`${Math.floor(item.interaction.duration / 60)}:${String(Math.floor(item.interaction.duration % 60)).padStart(2, '0')}`}
          </div>
        )
      },
      {
        key: 'analysis_status',
        header: 'Analysis',
        width: '1.75fr',
        sortable: false,
        render: (item: InteractionData) => (
          <PostCallStatus
            job={postCallJobs[item.interaction.id] || null}
            compact
          />
        )
      },
      {
        key: 'key_moments',
        header: 'Key Moments',
        width: '2fr',
        sortable: false,
        render: (item: InteractionData) => {
          const timelineResponse = item.transcript as ApiTimelineResponse;
          const moments = timelineResponse?.mmts || {};
          const momentsList = Object.values(moments) as MomentData[];
          const positiveMoment = momentsList.find(m => m.sentiment.toLowerCase().includes('positive'));
          const negativeMoment = momentsList.find(m => m.sentiment.toLowerCase().includes('negative'));

          return (Object.keys(moments).length > 0 && (positiveMoment || negativeMoment)) ? (
            <div className="section-scores-piles">
              <div className="piles-container">
                <div className="pile-column">
                  <div className="pile-items">
                    {positiveMoment && (
                      <PileItem
                        text={positiveMoment.name}
                        icon={getSentimentIcon(positiveMoment.sentiment)}
                        category="success"
                        showIcon={true}
                        title={positiveMoment.name}
                      />
                    )}
                    {negativeMoment && (
                      <PileItem
                        text={negativeMoment.name}
                        icon={getSentimentIcon(negativeMoment.sentiment)}
                        category="danger"
                        showIcon={true}
                        title={negativeMoment.name}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="section-scores-empty">No key moments available</div>
          );
        }
      },
      {
        key: 'section_scores',
        header: 'Top/Bottom Sections',
        width: '2.5fr',
        sortable: false,
        render: (item: InteractionData) => {
          const interactionId = item.interaction.id;
          const interactionSectionScores = sectionScores[interactionId] || {};
          
          return interactionSectionScores && Object.keys(interactionSectionScores).length > 0 ? (
            <SectionScoresBar 
              scores={formatSectionScores(interactionSectionScores)} 
              maxScore={20}
              viewMode="piles"
            />
          ) : (
            <div className="section-scores-empty">No section scores available</div>
          );
        }
      },
      {
        key: 'timeline',
        header: 'Transcript',
        subtext: <div className="timeline-header-subtext">
          <div className="timeline-legend-item">
            <div className="timeline-legend-box agent-box"></div>
            <span>Agent</span>
          </div>
          <div className="timeline-legend-item">
            <div className="timeline-legend-box customer-box"></div>
            <span>Customer</span>
          </div>
          <div className="timeline-legend-item">
            <div className="timeline-legend-box silence-box"></div>
            <span>Silence</span>
          </div>
        </div>,
        width: '2.75fr',
        sortable: false,
        allowOverflow: true,
        render: (item: InteractionData) => (
          // <div className="timeline-wrapper"> {/* This wrapper is needed to ensure the timeline is visible */}

          <MiniTimeline 
            interactionId={item.interaction.id} 
            timelineData={timelineData[item.interaction.id] || []}
            mmts={(item.transcript as ApiTimelineResponse)?.mmts}
          />
          // </div>

        )
      },
      {
        key: 'call_score',
        header: 'Call Score',
        width: '1fr',
        sortable: true,
        render: (item: InteractionData) => {
          const callScoreData = getKpiData(item.interaction.id, 'call_score');
          if (callScoreData && callScoreData.score) {
            const scoreValue = typeof callScoreData.score.score === 'string' 
              ? parseFloat(callScoreData.score.score) 
              : callScoreData.score.score;
            
            // Create custom tooltip with threshold information
            const tooltip = `Good ≥${callScoreData.score.threshold_high}, Warning ${callScoreData.score.threshold_low}-${callScoreData.score.threshold_high-1}, Poor <${callScoreData.score.threshold_low}`;
            
            return (
              <div className="numeric-label-container">
                <NumericLabel 
                  value={scoreValue} 
                  mode="rating"
                  warningThreshold={callScoreData.score.threshold_high} 
                  dangerThreshold={callScoreData.score.threshold_low} 
                  showZero={true}
                  tooltip={tooltip}
                />
              </div>
            );
          } else {
            return <div className="call-score-empty">-</div>;
          }
        }
      },
      {
        key: 'violations',
        header: 'Violations',
        width: '1.25fr',
        sortable: true,
        render: (item: InteractionData) => {
          const violationsData = getKpiData(item.interaction.id, 'script_violations');
          if (violationsData && violationsData.score) {
            const violationCount = typeof violationsData.score.score === 'string' 
              ? parseFloat(violationsData.score.score) 
              : violationsData.score.score;
            
            // Create custom tooltip with threshold information
            const tooltip = `Good =${violationsData.score.threshold_low}, Poor >${violationsData.score.threshold_high}`;
            
            return (
              <div className="numeric-label-container">
                <NumericLabel 
                  value={violationCount} 
                  mode="violations"
                  warningThreshold={violationsData.score.threshold_low} 
                  dangerThreshold={violationsData.score.threshold_high} 
                  showZero={true}
                  tooltip={tooltip}
                />
              </div>
            );
          } else {
            return <span className="violations-tbd">-</span>;
          }
        }
      }
    ],
    filterConfig: interactionFilters,
    searchPlaceholder: "Search Interactions",
    showSearch: true,
    showFilterButton: true,
    showFilterBar: true,
    itemsPerPage: 10,
    defaultSort: {
      field: 'id',
      direction: 'asc'
    }
  };


  return (
    <SearchList
      config={searchListConfig}
      data={interactions}
      loading={loading}
      error={error}
      filters={filters}
      searchQuery={searchQuery}
      currentPage={currentPage}
      totalPages={totalPages}
      itemsPerPage={itemsPerPage}
      sortConfig={sortConfig}
      onSearchChange={setSearchQuery}
      onSearchSubmit={handleSearch}
      onFilterChange={handleFilterChange}
      onFilterActionChange={() => {}}
      onPageChange={handlePageChangeWithFetch}
      onSort={handleSortWithFetch}
      onItemClick={(item: InteractionData) => handleInteractionClick(item.interaction.id)}
      emptyMessage="No interactions found"
      loadingMessage="Loading interactions..."
      footer={(item: InteractionData) => {
        const mmtCount = countExecutions(item, 'mmt');
        const evalCount = countExecutions(item, 'eval');
        const summaryCount = countExecutions(item, 'summary');
        const adherenceCount = countExecutions(item, 'adherence');
        
        return (
          <div className="footer-notes">
            <div className="notes-list">
              <span
                className="note-item"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/execution/mmt?iid=${item.interaction.id}`);
                }}
                style={{ cursor: 'pointer' }}
                title={`Moment Executions: ${mmtCount}`}
              >
                Moment Executions: {mmtCount}
              </span>
              <span
                className="note-item"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/execution/eval?iid=${item.interaction.id}`);
                }}
                style={{ cursor: 'pointer' }}
                title={`Form Evaluation Executions: ${evalCount}`}
              >
                Form Evaluation Executions: {evalCount}
              </span>
              <span
                className="note-item"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/execution/summary?iid=${item.interaction.id}`);
                }}
                style={{ cursor: 'pointer' }}
                title={`Summary Executions: ${summaryCount}`}
              >
                Summary Executions: {summaryCount}
              </span>
              <span
                className="note-item"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/execution/adherence?iid=${item.interaction.id}`);
                }}
                style={{ cursor: 'pointer' }}
                title={`Script Adherence Executions: ${adherenceCount}`}
              >
                Script Adherence Executions: {adherenceCount}
              </span>
              <span
                className="note-item"
                onClick={(e) => void handleReplayClick(item.interaction.id, e)}
                style={{ cursor: 'pointer' }}
                title="Replay interaction (prepare TA if needed)"
              >
                Replay
              </span>
              {getTranscriptId(item) != null ? (
                <span
                  className="note-item"
                  onClick={(e) => void handleDownloadTranscriptClick(item.interaction.id, e)}
                  style={{ cursor: 'pointer' }}
                  title="Download transcript (.txt, Assembly line format)"
                >
                  Download transcript
                </span>
              ) : null}
            </div>
          </div>
        );
      }}
    />
  );
};

export default InteractionSearchList; 